/**
 * /ops/file-backups - the tenant file backup (OCP-423). The page the
 * file-backup alerts link to: is the newest complete run fresh, the last
 * result per bucket, repository size vs backup host disk, the target setup,
 * restores and the file restore drill. Every action uses the same REST route
 * as its `ocp_*` MCP tool and is audited with a reason:
 *   - back up now (all buckets or one), verify / set up the target,
 *   - restore a bucket or one object (preview -> confirmation token -> restore),
 *   - run the file restore drill,
 *   - reveal the repository password for offline escrow (a person only).
 * Runbook: deploy/README.md, section "Tenant file backups".
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface RunRow {
  id: string;
  trigger: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  actor_email: string | null;
  only_bucket: string | null;
  bucket_count: number;
  ok_count: number;
  failed_count: number;
  bytes_added: number | null;
  repo_size_bytes: number | null;
  forget_status: string | null;
  prune_status: string | null;
  duration_ms: number | null;
  error: string | null;
  deferred_reason: string | null;
}
interface BucketRow {
  bucket: string;
  tenant_id: string | null;
  status: string;
  snapshot_id: string | null;
  snapshot_time: string | null;
  bytes_added: number | null;
  bytes_processed: number | null;
  files_processed: number | null;
  duration_ms: number | null;
  error: string | null;
  started_at: string;
}
interface RestoreRow {
  id: string;
  kind: 'bucket' | 'object';
  bucket: string;
  key: string | null;
  snapshot_id: string;
  mode: string;
  status: string;
  actor_email: string;
  reason: string;
  source: string;
  created_at: string;
  copied: number | null;
  error_count: number | null;
  bytes_restored: number | null;
  error: string | null;
}
interface DrillRow {
  id: string;
  status: string;
  trigger: string;
  started_at: string;
  finished_at: string | null;
  phase: string | null;
  checks: Array<{ name: string; expected: number | string | null; actual: number | string | null; ok: boolean }>;
  error: string | null;
  files: { bucket: string | null; snapshot_id: string | null; missing: string[] };
}
interface Target {
  configured: boolean;
  source?: { id: string; hostname: string | null; ssh_host: string | null } | null;
  target?: { id: string; hostname: string | null; ssh_host: string | null } | null;
  repository?: string;
  setup_status?: string;
  setup_error?: string | null;
  setup_finished_at?: string | null;
  restic_version?: string | null;
  rest_server_version?: string | null;
  repo_id?: string | null;
  verified_at?: string | null;
  verify_ok?: string | null;
  password_revealed_at?: string | null;
  password_revealed_by?: string | null;
}
interface Overview {
  enabled: boolean;
  schedule: string;
  retention: { daily: number; weekly: number };
  prune_weekday: number;
  max_age_hours: number;
  disk_alert_percent: number;
  target: Target;
  newest_ok_at: string | null;
  stale: boolean;
  repository: { size_bytes: number | null; snapshot_count: number | null; target_total_bytes: number | null; target_free_bytes: number | null; target_used_percent: number | null; measured_at: string | null };
  last_run: RunRow | null;
  buckets: BucketRow[];
  runs: RunRow[];
  restores: RestoreRow[];
  drill: { enabled: boolean; schedule: string; last_passed_at: string | null };
  drills: DrillRow[];
  open_alerts: string[];
  integrity?: { ok: boolean; listed: number; summary: string; issues: Array<{ id: string; kind: string; time: string; hostname: string; tags: string[] }> } | null;
  previous_repos?: Array<{ repo_id: string; snapshots: number; tenants: number; last_snapshot: string | null; retired: boolean }>;
}
interface Snapshot {
  id: string;
  short_id: string;
  time: string;
  bytes: number | null;
}
interface RestorePreview {
  kind: 'bucket' | 'object';
  bucket: string;
  key: string | null;
  mode: 'missing_only' | 'overwrite';
  snapshot: { id: string; short_id: string; time: string; bytes: number | null; files: number | null };
  scratch_bytes_needed: number | null;
  live_bucket_exists: boolean;
  effect: string;
  confirmation_token: string;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const gb = (n: number | null | undefined) => (n == null ? '-' : n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(1)} GB` : `${(n / 1024 ** 2).toFixed(1)} MB`);
const secs = (ms: number | null) => (ms == null ? '-' : ms >= 120_000 ? `${Math.round(ms / 60_000)} min` : `${Math.round(ms / 1000)} s`);
const tone = (s: string) =>
  s === 'ok' || s === 'passed' || s === 'ready' ? 'text-green-700 dark:text-green-400' : s === 'failed' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400';
const inputCls = 'mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm';
const btn = 'rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm disabled:opacity-50';

/** OCP-930 - POST .../target/setup/preview: both hosts as checked now + the single-use confirmation. */
interface SetupPreviewHost {
  server_id: string;
  ssh_host: string;
  ssh_port: number;
  ssh_user: string;
  host_key_fingerprint: string;
}
interface SetupPreview {
  source: SetupPreviewHost;
  target: SetupPreviewHost;
  effect: string;
  confirmation_token: string;
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) {
    const b = e.body as { message?: string; error?: string } | null;
    return b?.message ?? b?.error ?? `Failed (HTTP ${e.status}).`;
  }
  return 'Failed.';
}

export function AdminFileBackupsPage() {
  const [data, setData] = useState<Overview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [bucket, setBucket] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [sourceId, setSourceId] = useState('');
  const [targetId, setTargetId] = useState('');
  // OCP-930 - the setup sends fleet storage credentials to both hosts: preview
  // (endpoints + live host keys) -> single-use confirmation -> setup.
  const [setupPreview, setSetupPreview] = useState<SetupPreview | null>(null);

  const load = useCallback(async () => {
    try {
      const o = await api<Overview>('/v1/admin/ops/file-backups');
      setData(o);
      setSourceId((v) => v || o.target.source?.id || '');
      setTargetId((v) => v || o.target.target?.id || '');
    } catch (e) {
      setErr(e instanceof ApiError ? `Could not load (HTTP ${e.status}).` : 'Could not load.');
    }
  }, []);
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 20_000);
    return () => clearInterval(t);
  }, [load]);

  const okReason = reason.trim().length >= 3;
  async function act(kind: 'run' | 'verify' | 'setup' | 'drill') {
    if (busy || !okReason) return;
    setBusy(kind);
    setMsg(null);
    try {
      if (kind === 'run') {
        await api('/v1/admin/ops/file-backups/run', { method: 'POST', body: { reason: reason.trim(), ...(bucket.trim() ? { bucket: bucket.trim() } : {}) } });
        setMsg('File backup enqueued - it appears under Runs while it runs.');
      } else if (kind === 'verify') {
        const r = await api<{ ok: boolean; report: Record<string, unknown> }>('/v1/admin/ops/file-backups/target/verify', { method: 'POST', body: { reason: reason.trim() } });
        setMsg(r.ok ? 'Target verified: repository opens with the sealed password, repository id matches.' : `Verify failed: ${String(r.report.error ?? 'see the target report')}`);
      } else if (kind === 'setup') {
        const body = { reason: reason.trim(), source_server_id: sourceId.trim(), target_server_id: targetId.trim() };
        if (!setupPreview) {
          setSetupPreview(await api<SetupPreview>('/v1/admin/ops/file-backups/target/setup/preview', { method: 'POST', body }));
          return;
        }
        await api('/v1/admin/ops/file-backups/target/setup', { method: 'POST', body: { ...body, confirmation_token: setupPreview.confirmation_token } });
        setSetupPreview(null);
        setMsg('Setup enqueued - the target status changes to ready or failed in a minute or two.');
      } else {
        await api('/v1/admin/ops/restore-drills/run', { method: 'POST', body: { reason: reason.trim(), kind: 'files', ...(bucket.trim() ? { bucket: bucket.trim() } : {}) } });
        setMsg('File restore drill enqueued - it appears under Drills when it finishes.');
      }
      setReason('');
      await load();
    } catch (e) {
      if (kind === 'setup') setSetupPreview(null);
      setMsg(errMsg(e));
    } finally {
      setBusy(null);
    }
  }

  const t = data?.target;
  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-lg font-semibold">File backups</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Tenant attachments in the object storage: one encrypted, deduplicated restic snapshot per bucket every night through an append-only
          rest-server on the separate backup host (the live host can add snapshots, never delete them),
          {data ? ` ${data.retention.daily} daily + ${data.retention.weekly} weekly kept,` : ''} no bucket replication (a replica would mirror deletions and
          ransomware). Restores go through a scratch RustFS and the S3 API and never delete a live object. Runbook: deploy/README.md, section
          &quot;Tenant file backups&quot;.
        </p>
      </div>
      {err && <p className="text-sm text-red-700 dark:text-red-400">{err}</p>}

      {data && t && (
        <>
          {data.open_alerts.length > 0 && (
            <p className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 p-2 text-sm" data-testid="file-backup-alerts">
              Open alerts: {data.open_alerts.join(', ')}
            </p>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="file-backup-overview">
            <Card label="Newest complete run" value={data.newest_ok_at ? fmtDateTime(data.newest_ok_at) : 'none yet'} bad={data.stale} hint={data.stale ? `older than ${data.max_age_hours} h - alerting` : `max age ${data.max_age_hours} h`} />
            <Card label="Target" value={t.configured ? (t.setup_status ?? '-') : 'not configured'} bad={!t.configured || t.setup_status !== 'ready'} hint={t.setup_error ?? t.repository ?? 'set up below'} />
            <Card label="Repository" value={gb(data.repository.size_bytes)} bad={false} hint={`${data.repository.snapshot_count ?? '-'} snapshots`} />
            <Card
              label="Backup host disk"
              value={data.repository.target_used_percent == null ? '-' : `${data.repository.target_used_percent}% used`}
              bad={(data.repository.target_used_percent ?? 0) >= data.disk_alert_percent}
              hint={`${gb(data.repository.target_free_bytes)} free of ${gb(data.repository.target_total_bytes)}, alert at ${data.disk_alert_percent}%`}
            />
            <Card label="Schedule" value={data.enabled ? data.schedule : 'off'} bad={!data.enabled} hint={`UTC, prune on ${WEEKDAYS[data.prune_weekday] ?? data.prune_weekday}`} />
            <Card label="Last passed file drill" value={data.drill.last_passed_at ? fmtDateTime(data.drill.last_passed_at) : 'none yet'} bad={!data.drill.last_passed_at} hint={data.drill.enabled ? `schedule ${data.drill.schedule} (UTC)` : 'schedule off'} />
          </div>

          <div className="space-y-2 rounded border border-gray-200 dark:border-gray-800 p-3">
            <div className="flex flex-wrap items-end gap-2">
              <label className="min-w-[16rem] flex-1 text-xs text-gray-600 dark:text-gray-400">
                Reason (audited)
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. before the storage host migration" className={inputCls} data-testid="fb-reason" />
              </label>
              <label className="text-xs text-gray-600 dark:text-gray-400">
                Bucket (optional)
                <input value={bucket} onChange={(e) => setBucket(e.target.value)} placeholder="all buckets / random drill" className={`${inputCls} w-80 font-mono text-xs`} data-testid="fb-bucket" />
              </label>
              <button type="button" disabled={!!busy || !okReason} onClick={() => void act('run')} className="rounded bg-brand-600 px-3 py-1.5 text-sm text-white disabled:opacity-50" data-testid="fb-run">
                Back up now
              </button>
              <button type="button" disabled={!!busy || !okReason} onClick={() => void act('drill')} className={btn} data-testid="fb-drill">
                Run file drill
              </button>
              <button type="button" disabled={!!busy || !okReason || !t.configured} onClick={() => void act('verify')} className={btn} data-testid="fb-verify">
                Verify target
              </button>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-gray-600 dark:text-gray-400">
                Live storage host (server id)
                <input value={sourceId} onChange={(e) => { setSourceId(e.target.value); setSetupPreview(null); }} className={`${inputCls} w-80 font-mono text-xs`} data-testid="fb-source" />
              </label>
              <label className="text-xs text-gray-600 dark:text-gray-400">
                Backup host (server id)
                <input value={targetId} onChange={(e) => { setTargetId(e.target.value); setSetupPreview(null); }} className={`${inputCls} w-80 font-mono text-xs`} data-testid="fb-target" />
              </label>
              <button type="button" disabled={!!busy || !okReason || !sourceId.trim() || !targetId.trim()} onClick={() => void act('setup')} className={btn} data-testid="fb-setup">
                {setupPreview ? 'Confirm setup' : t.configured ? 'Preview repair / re-run setup' : 'Preview target setup'}
              </button>
              {setupPreview && (
                <div data-testid="fb-setup-preview" className="w-full rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-2 text-xs text-amber-900 dark:text-amber-200">
                  {(['source', 'target'] as const).map((role) => {
                    const h = setupPreview[role];
                    return (
                      <div key={role}>
                        {role === 'source' ? 'Live storage host' : 'Backup host'}: {h.ssh_user}@{h.ssh_host}:{h.ssh_port} <span className="font-mono break-all">{h.host_key_fingerprint}</span>
                      </div>
                    );
                  })}
                  <div className="mt-1">{setupPreview.effect} The confirmation is valid once, for 10 minutes. A super_admin person with a second factor only (OCP-930).</div>
                </div>
              )}
              <span className="text-[11px] text-gray-500">
                {t.source?.hostname ?? '-'} {'->'} {t.target?.hostname ?? '-'}
                {t.restic_version ? ` · restic ${t.restic_version}` : ''}
                {t.rest_server_version ? ` · rest-server ${t.rest_server_version} (append-only)` : ''}
                {t.repo_id ? ` · repo ${t.repo_id}` : ''}
                {t.verified_at ? ` · verified ${fmtDateTime(t.verified_at)} (${t.verify_ok})` : ''}
              </span>
            </div>
            {msg && <p className="text-xs text-gray-600 dark:text-gray-400" data-testid="fb-msg">{msg}</p>}
          </div>

          <BucketTable buckets={data.buckets} onDone={load} />

          <section>
            <h2 className="mb-2 text-sm font-semibold">Runs</h2>
            <table className="w-full text-left text-xs">
              <thead className="text-gray-500">
                <tr><th className="py-1">Started</th><th>Status</th><th>Buckets</th><th>Added</th><th>Repository</th><th>Forget / prune</th><th>Duration</th><th>By</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {data.runs.map((r) => (
                  <tr key={r.id} className="align-top" data-testid={`fb-run-${r.status}`}>
                    <td className="py-1">{fmtDateTime(r.started_at)}</td>
                    <td className={tone(r.status)}>
                      {r.status}
                      {r.error && <div className="max-w-md text-[11px]">{r.error}</div>}
                      {r.deferred_reason && <div className="max-w-md text-[11px]">deferred: {r.deferred_reason}</div>}
                    </td>
                    <td>{r.only_bucket ? `${r.only_bucket} ` : ''}{r.ok_count}/{r.bucket_count} ok{r.failed_count ? `, ${r.failed_count} failed` : ''}</td>
                    <td>{gb(r.bytes_added)}</td>
                    <td>{gb(r.repo_size_bytes)}</td>
                    <td>{r.forget_status ?? '-'} / {r.prune_status ?? '-'}</td>
                    <td>{secs(r.duration_ms)}</td>
                    <td>{r.trigger === 'cron' ? 'schedule' : r.trigger === 'deferred' ? 'deferred' : r.actor_email}</td>
                  </tr>
                ))}
                {data.runs.length === 0 && <tr><td colSpan={8} className="py-2 text-gray-500">No run yet.</td></tr>}
              </tbody>
            </table>
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold">Restores</h2>
            <table className="w-full text-left text-xs">
              <thead className="text-gray-500">
                <tr><th className="py-1">Requested</th><th>What</th><th>Snapshot</th><th>Mode</th><th>Status</th><th>Copied</th><th>By / why</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {data.restores.map((r) => (
                  <tr key={r.id} className="align-top" data-testid={`fb-restore-${r.status}`}>
                    <td className="py-1">{fmtDateTime(r.created_at)}</td>
                    <td className="font-mono">{r.bucket}{r.key ? ` / ${r.key}` : ''}</td>
                    <td className="font-mono">{r.snapshot_id.slice(0, 8)}</td>
                    <td>{r.mode}</td>
                    <td className={tone(r.status)}>{r.status}{r.error && <div className="max-w-md text-[11px]">{r.error}</div>}</td>
                    <td>{r.copied ?? '-'}{r.bytes_restored != null ? ` (${gb(r.bytes_restored)})` : ''}{r.error_count ? `, ${r.error_count} errors` : ''}</td>
                    <td>{r.actor_email} ({r.source}): {r.reason}</td>
                  </tr>
                ))}
                {data.restores.length === 0 && <tr><td colSpan={7} className="py-2 text-gray-500">No restore yet.</td></tr>}
              </tbody>
            </table>
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold">File restore drills</h2>
            <table className="w-full text-left text-xs">
              <thead className="text-gray-500">
                <tr><th className="py-1">Started</th><th>Bucket</th><th>Status</th><th>Checks</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {data.drills.map((d) => (
                  <tr key={d.id} className="align-top" data-testid={`fb-drill-${d.status}`}>
                    <td className="py-1">{fmtDateTime(d.started_at)}</td>
                    <td className="font-mono">{d.files.bucket ?? '-'}{d.files.snapshot_id ? ` @ ${d.files.snapshot_id.slice(0, 8)}` : ''}</td>
                    <td className={tone(d.status)}>{d.status}{d.status === 'running' && d.phase ? ` (${d.phase})` : ''}{d.error && <div className="max-w-md text-[11px]">{d.error}</div>}</td>
                    <td>
                      <ul className="font-mono">
                        {d.checks.map((c) => (
                          <li key={c.name} className={c.ok ? '' : 'text-red-700 dark:text-red-400'}>{c.name}: {String(c.actual)}{c.expected !== null ? ` ${c.ok ? '=' : '!='} ${String(c.expected)}` : ''}</li>
                        ))}
                      </ul>
                      {d.files.missing.length > 0 && <div className="text-[11px] text-red-700 dark:text-red-400">missing: {d.files.missing.join(', ')}</div>}
                    </td>
                  </tr>
                ))}
                {data.drills.length === 0 && <tr><td colSpan={4} className="py-2 text-gray-500">No file drill yet.</td></tr>}
              </tbody>
            </table>
          </section>

          <IntegrityPanel integrity={data.integrity ?? null} onDone={load} />
          <PreviousRepos repos={data.previous_repos ?? []} onDone={load} />
          <PasswordEscrow target={t} onDone={load} />
        </>
      )}
    </div>
  );
}

/** The last result per bucket; each row opens the two-step restore. */
function BucketTable({ buckets, onDone }: { buckets: BucketRow[]; onDone: () => Promise<void> }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold">Buckets (last result)</h2>
      <table className="w-full text-left text-xs">
        <thead className="text-gray-500">
          <tr><th className="py-1">Bucket</th><th>Status</th><th>Snapshot</th><th>Added / size</th><th>Files</th><th>Duration</th><th /></tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {buckets.map((b) => (
            <tr key={b.bucket} className="align-top" data-testid={`fb-bucket-${b.bucket}`}>
              <td className="py-1 font-mono">{b.bucket}</td>
              <td className={tone(b.status)}>{b.status}{b.error && <div className="max-w-md text-[11px]">{b.error}</div>}</td>
              <td className="font-mono">{b.snapshot_id ? `${b.snapshot_id.slice(0, 8)} ${b.snapshot_time ? fmtDateTime(b.snapshot_time) : ''}` : '-'}</td>
              <td>{gb(b.bytes_added)} / {gb(b.bytes_processed)}</td>
              <td>{b.files_processed ?? '-'}</td>
              <td>{secs(b.duration_ms)}</td>
              <td>
                {b.bucket !== '.rustfs.sys' && (
                  <button type="button" className="text-brand-700 underline dark:text-brand-400" onClick={() => setOpen(open === b.bucket ? null : b.bucket)} data-testid={`fb-restore-open-${b.bucket}`}>
                    Restore...
                  </button>
                )}
                {open === b.bucket && <RestorePanel bucket={b.bucket} onDone={onDone} />}
              </td>
            </tr>
          ))}
          {buckets.length === 0 && <tr><td colSpan={7} className="py-2 text-gray-500">No bucket backed up yet.</td></tr>}
        </tbody>
      </table>
    </section>
  );
}

/** Two-step restore: preview (snapshot + effect + token), then restore with a reason. */
export function RestorePanel({ bucket, onDone }: { bucket: string; onDone: () => Promise<void> }) {
  const [kind, setKind] = useState<'bucket' | 'object'>('bucket');
  const [key, setKey] = useState('');
  const [mode, setMode] = useState<'missing_only' | 'overwrite'>('missing_only');
  const [snapshots, setSnapshots] = useState<Snapshot[] | null>(null);
  const [snapshot, setSnapshot] = useState('');
  const [preview, setPreview] = useState<RestorePreview | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api<{ snapshots: Snapshot[] }>(`/v1/admin/ops/file-backups/snapshots?bucket=${encodeURIComponent(bucket)}`)
      .then((r) => alive && setSnapshots(r.snapshots))
      .catch((e: unknown) => alive && setMsg(errMsg(e)));
    return () => {
      alive = false;
    };
  }, [bucket]);

  const body = () => ({ kind, bucket, ...(kind === 'object' ? { key: key.trim() } : {}), mode, ...(snapshot ? { snapshot_id: snapshot } : {}) });
  async function doPreview() {
    setBusy(true);
    setMsg(null);
    try {
      setPreview(await api<RestorePreview>('/v1/admin/ops/file-backups/restore/preview', { method: 'POST', body: body() }));
    } catch (e) {
      setMsg(errMsg(e));
    } finally {
      setBusy(false);
    }
  }
  async function doRestore() {
    if (!preview) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api<{ restore_id: string }>('/v1/admin/ops/file-backups/restore', { method: 'POST', body: { ...body(), reason: reason.trim(), confirmation_token: preview.confirmation_token } });
      setMsg(`Restore ${r.restore_id} enqueued - it shows under Restores.`);
      setPreview(null);
      setReason('');
      await onDone();
    } catch (e) {
      setMsg(errMsg(e));
    } finally {
      setBusy(false);
    }
  }
  const changed = () => setPreview(null);

  return (
    <div className="mt-1 w-[28rem] space-y-1.5 rounded border border-gray-200 dark:border-gray-800 p-2" data-testid="fb-restore-panel">
      <div className="flex gap-2">
        <select value={kind} onChange={(e) => { setKind(e.target.value as 'bucket' | 'object'); changed(); }} className={inputCls} data-testid="fb-restore-kind">
          <option value="bucket">whole bucket</option>
          <option value="object">one object</option>
        </select>
        <select value={mode} onChange={(e) => { setMode(e.target.value as 'missing_only' | 'overwrite'); changed(); }} className={inputCls} data-testid="fb-restore-mode">
          <option value="missing_only">only missing objects</option>
          <option value="overwrite">also replace changed objects</option>
        </select>
      </div>
      {kind === 'object' && <input value={key} onChange={(e) => { setKey(e.target.value); changed(); }} placeholder="object key" className={`${inputCls} font-mono text-xs`} data-testid="fb-restore-key" />}
      <select value={snapshot} onChange={(e) => { setSnapshot(e.target.value); changed(); }} className={inputCls} data-testid="fb-restore-snapshot">
        <option value="">newest snapshot</option>
        {(snapshots ?? []).map((s) => (
          <option key={s.id} value={s.id}>{s.short_id} - {fmtDateTime(s.time)} ({gb(s.bytes)})</option>
        ))}
      </select>
      {!preview ? (
        <button type="button" disabled={busy || (kind === 'object' && !key.trim())} onClick={() => void doPreview()} className={btn} data-testid="fb-restore-preview">
          Preview
        </button>
      ) : (
        <>
          <p className="text-[11px]" data-testid="fb-restore-effect">
            {preview.effect} Snapshot {preview.snapshot.short_id} from {fmtDateTime(preview.snapshot.time)}
            {preview.scratch_bytes_needed != null ? `, scratch space ${gb(preview.scratch_bytes_needed)}` : ', scratch space sized on the host from the object'}.
          </p>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reason (audited, min. 10 characters)" className={inputCls} data-testid="fb-restore-reason" />
          <button type="button" disabled={busy || reason.trim().length < 10} onClick={() => void doRestore()} className="rounded bg-red-600 px-3 py-1.5 text-sm text-white disabled:opacity-50" data-testid="fb-restore-go">
            Restore into the live bucket
          </button>
        </>
      )}
      {msg && <p className="text-[11px] text-gray-600 dark:text-gray-400" data-testid="fb-restore-msg">{msg}</p>}
    </div>
  );
}

/** Re-review 1 - snapshots in the repository OCP did not record block forget / prune; forget them two-step. */
function IntegrityPanel({ integrity, onDone }: { integrity: NonNullable<Overview['integrity']> | null; onDone: () => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  if (!integrity || integrity.ok) return null;
  const ids = integrity.issues.map((i) => i.id);
  async function step() {
    setMsg(null);
    try {
      if (!token) {
        const p = await api<{ confirmation_token: string; effect: string }>('/v1/admin/ops/file-backups/snapshots/forget-unrecorded/preview', { method: 'POST', body: { snapshot_ids: ids, reason: reason.trim() } });
        setToken(p.confirmation_token);
        setMsg(p.effect);
      } else {
        const r = await api<{ forgotten: number }>('/v1/admin/ops/file-backups/snapshots/forget-unrecorded', { method: 'POST', body: { snapshot_ids: ids, reason: reason.trim(), confirmation_token: token } });
        setMsg(`${r.forgotten} snapshot(s) forgotten.`);
        setToken(null);
        await onDone();
      }
    } catch (e) {
      setMsg(errMsg(e));
    }
  }
  return (
    <section className="rounded border border-red-200 dark:border-red-900/50 p-3" data-testid="fb-integrity">
      <h2 className="text-sm font-semibold">Repository integrity: forget and prune are blocked</h2>
      <p className="text-xs text-gray-600 dark:text-gray-400">{integrity.summary}. Snapshots OCP did not write (a planted future date could push the genuine history out of the retention). Check them, then forget them.</p>
      <ul className="mt-1 font-mono text-[11px]">
        {integrity.issues.map((i) => (
          <li key={i.id}>{i.kind} {i.id.slice(0, 12)} - {i.hostname} {i.tags.join(' ')} {i.time}</li>
        ))}
      </ul>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reason (audited, min. 10 characters)" className={`${inputCls} max-w-md`} data-testid="fb-integrity-reason" />
        <button type="button" disabled={reason.trim().length < 10} onClick={() => void step()} className={btn} data-testid="fb-integrity-forget">
          {token ? 'Confirm: forget these snapshots' : 'Forget these snapshots'}
        </button>
      </div>
      {msg && <p className="mt-1 text-[11px]" data-testid="fb-integrity-msg">{msg}</p>}
    </section>
  );
}

/** Re-review 4 - repositories on former backup hosts still holding recorded snapshots; retire once erased by hand. */
function PreviousRepos({ repos, onDone }: { repos: NonNullable<Overview['previous_repos']>; onDone: () => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  if (repos.length === 0) return null;
  async function retire(repoId: string) {
    setMsg(null);
    try {
      await api(`/v1/admin/ops/file-backups/repos/${repoId}/retire`, { method: 'POST', body: { reason: reason.trim(), confirm_erased: true } });
      setMsg(`Repository ${repoId} recorded as erased.`);
      await onDone();
    } catch (e) {
      setMsg(errMsg(e));
    }
  }
  return (
    <section className="rounded border border-gray-200 dark:border-gray-800 p-3" data-testid="fb-previous-repos">
      <h2 className="text-sm font-semibold">Repositories on former backup hosts</h2>
      <p className="text-xs text-gray-600 dark:text-gray-400">Tenant purges stay open while a former repository still holds their snapshots. Erase them on the old host (or wipe it), then record it here.</p>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reason (audited, min. 10 characters)" className={`${inputCls} mt-1 max-w-md`} data-testid="fb-retire-reason" />
      <table className="mt-1 w-full text-left text-xs">
        <tbody>
          {repos.map((r) => (
            <tr key={r.repo_id}>
              <td className="font-mono">{r.repo_id}</td>
              <td>{r.snapshots} snapshots, {r.tenants} tenants{r.last_snapshot ? `, last ${fmtDateTime(r.last_snapshot)}` : ''}</td>
              <td>{r.retired ? 'retired' : <button type="button" disabled={reason.trim().length < 10} onClick={() => void retire(r.repo_id)} className={btn} data-testid={`fb-retire-${r.repo_id}`}>I confirm it is erased</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {msg && <p className="mt-1 text-[11px]">{msg}</p>}
    </section>
  );
}

/** Key escrow: the repository password, shown once to a person after a two-step confirm. */
function PasswordEscrow({ target, onDone }: { target: Target; onDone: () => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  if (!target.configured) return null;
  async function step() {
    setMsg(null);
    try {
      if (!token) {
        const p = await api<{ confirmation_token: string; warning: string }>('/v1/admin/ops/file-backups/repository-password/reveal/preview', { method: 'POST', body: { reason: reason.trim() } });
        setToken(p.confirmation_token);
        setWarning(p.warning);
      } else {
        const r = await api<{ repository_password: string }>('/v1/admin/ops/file-backups/repository-password/reveal', { method: 'POST', body: { reason: reason.trim(), confirmation_token: token } });
        setPassword(r.repository_password);
        setToken(null);
        await onDone();
      }
    } catch (e) {
      setMsg(errMsg(e));
    }
  }
  return (
    <section className="rounded border border-amber-200 dark:border-amber-900/50 p-3">
      <h2 className="text-sm font-semibold">Repository password (key escrow)</h2>
      <p className="text-xs text-gray-600 dark:text-gray-400">
        The backup is encrypted with a password only orboto Control holds (sealed). Losing it together with the control-plane database loses every
        file backup. Reveal it once and store it offline. Not available to bot accounts or through MCP.
        {target.password_revealed_at ? ` Last revealed ${fmtDateTime(target.password_revealed_at)} by ${target.password_revealed_by}.` : ' Never revealed yet.'}
      </p>
      {password ? (
        <p className="mt-2 break-all rounded bg-gray-100 dark:bg-gray-900 p-2 font-mono text-xs" data-testid="fb-password">{password}</p>
      ) : (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reason (audited, min. 10 characters)" className={`${inputCls} max-w-md`} data-testid="fb-reveal-reason" />
          <button type="button" disabled={reason.trim().length < 10} onClick={() => void step()} className={btn} data-testid="fb-reveal">
            {token ? 'Confirm: show the password' : 'Reveal password'}
          </button>
        </div>
      )}
      {warning && !password && <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-400">{warning}</p>}
      {msg && <p className="mt-1 text-[11px] text-red-700 dark:text-red-400">{msg}</p>}
    </section>
  );
}

function Card({ label, value, hint, bad }: { label: string; value: string; hint: string; bad: boolean }) {
  return (
    <div className={`rounded border p-3 ${bad ? 'border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-800'}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-medium">{value}</div>
      <div className="truncate text-[11px] text-gray-500" title={hint}>{hint}</div>
    </div>
  );
}
